import { ChangeEvent, FC, useState } from 'react'
import Link from 'next/link'
import useSWR from 'swr'
import {
  Heading,
  Button,
  Text,
  CheckboxField,
  Modal,
  InputField,
  Icon,
  ButtonType,
} from '@saasalerts/ui'
import { ReportFilters } from '@/components/Graphs'
import fetchJson from '@/utils/fetchJson'
import { htmlEmailBuilder } from '@/utils/htmlEmailBuilder'
import {
  formatToUSDate,
  getReportById,
  hasDateRangeFilter,
} from '@/utils/methods'
import { useClipboard } from 'use-clipboard-copy'
import { SecureReportLinkData } from '@/types/reports'
import {
  Partner,
  SCHEDULED_REPORT_BRANDING_DISPLAY,
} from '@saasalerts/common/shared'
import {
  faCopy,
  faCheck,
  faLink,
  faEnvelope,
} from '@awesome.me/kit-e270131d0a/icons/classic/regular'
import toast from 'react-hot-toast'
import { HideBranding } from '@/components/Tooltips'
import { WEBHOOK_EVENT } from '@/utils/constants'
import { sendManageInternalEvent } from '@/utils/sendManageInternalEvent'
import { Organization } from '@saasalerts/common/manage'

const ShareReportInfo = ({
  daysToExpire,
  onDaysToExpireChange,
}: {
  daysToExpire: number
  onDaysToExpireChange: (value: number) => void
}) => {
  const handleDaysToExpireChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = event.target.value as unknown as number
    // Ensure the input is a number
    if (!isNaN(inputValue)) {
      // Check if the number is within the range [1, 31]
      if (inputValue >= 1 && inputValue <= 31) {
        onDaysToExpireChange(inputValue)
      }
    }
  }

  return (
    <div className="prose-sm">
      <Text>
        Generate a secure link for the report that organization can only view by
        entering a unique access key. The link will automatically expire in
        <div className="mx-1 inline-block w-20">
          <InputField
            type="number"
            placeholder="7"
            value={daysToExpire}
            onChange={handleDaysToExpireChange}
            min={1}
            max={31}
          />
        </div>
        days. Organization will not be able to modify the date range and can
        only edit and print the interactive charts.
      </Text>
    </div>
  )
}

const GeneratedLinkBody: FC<
  React.PropsWithChildren<{
    tokenData: SecureReportLinkData | undefined
  }>
> = ({ tokenData }) => {
  const report = getReportById(tokenData?.reportId ?? '')
  const { data: organization } = useSWR<Organization>(
    `/api/organizations/${tokenData?.organizationId}`
  )
  const accessKeyClipboard = useClipboard({ copiedTimeout: 750 })
  const secureLinkClipboard = useClipboard({ copiedTimeout: 750 })
  return (
    <>
      <Text>
        Click Email Link and Email Access Key to share report access details
        with your organization.
      </Text>
      <Text>
        <Text as="span" weight="medium">
          Report: {''}
        </Text>
        {report?.name} <br />
        <Text as="span" weight="medium">
          Expires On: {''}
        </Text>
        {tokenData?.expiryDate} <br />
        {hasDateRangeFilter(report?.id) && (
          <Text>
            <Text as="span" weight="medium">
              Date Range: {''}
            </Text>
            {tokenData?.startDate} - {tokenData?.endDate}
          </Text>
        )}
        {organization?.name && (
          <Text>
            <Text as="span" weight="medium">
              Organization: {''}
            </Text>
            {organization?.name} <br />
          </Text>
        )}
        {tokenData?.accountEmail && (
          <Text>
            <Text as="span" weight="medium">
              Account Email: {''}
            </Text>
            {tokenData?.accountEmail} <br />
          </Text>
        )}
        <div className="flex items-start">
          <Text as="span" weight="medium">
            Access Key: {''}
          </Text>
          <button
            className="mx-2"
            onClick={() => accessKeyClipboard.copy(tokenData?.accessKey)}
          >
            {tokenData?.accessKey}
            {accessKeyClipboard.copied ? (
              <Icon
                icon={faCheck}
                className="mx-1 inline size-4"
                aria-hidden="true"
              />
            ) : (
              <Icon
                icon={faCopy}
                className="mx-1 inline size-4"
                aria-hidden="true"
              />
            )}
          </button>
        </div>
      </Text>
      <InputField
        type="text"
        label="Secure Link"
        name="secure link"
        value={tokenData?.shareLink}
        readOnly
        ref={secureLinkClipboard.target}
        Icon={secureLinkClipboard.copied ? faCheck : faCopy}
        onIconClick={secureLinkClipboard.copy}
        iconPosition="right"
      />
    </>
  )
}

export const ShareReport: FC<
  React.PropsWithChildren<{
    open: boolean
    setOpen: (open: boolean) => void
    filters?: ReportFilters
    reportId: string
    organizationId?: string
  }>
> = ({ open, setOpen, filters, reportId, organizationId = undefined }) => {
  const report = getReportById(reportId)
  const [linkGenerated, setLinkGenerated] = useState(false)
  const [daysToExpire, setDaysToExpire] = useState<number>(7)
  const [tokenData, setTokenData] = useState<SecureReportLinkData>()
  const [loading, setLoading] = useState(false)
  const [hideBranding, setHideBranding] = useState<boolean>(false)
  const { data: partnerProfile } = useSWR<Partner>('/api/partner')
  const onGenerateSecureLink = async () => {
    try {
      setLoading(true)
      const res = await fetchJson<SecureReportLinkData>(
        `/api/partner/generate-secure-link`,
        {
          method: 'POST',
          body: JSON.stringify({
            ...filters,
            reportId,
            organizationId,
            linkExpirationDays: daysToExpire,
            origin: window.location.origin,
            branding: hideBranding
              ? SCHEDULED_REPORT_BRANDING_DISPLAY.HIDE
              : SCHEDULED_REPORT_BRANDING_DISPLAY.SHOW,
          }),
        }
      )

      setLoading(false)
      setTokenData(res)
      setLinkGenerated(true)
      sendManageInternalEvent({
        reportId,
        eventType: WEBHOOK_EVENT.REPORTS_REPORT_SHARED,
        filters,
      })
    } catch (error) {
      setLoading(false)
      toast.error((error as Error)?.message)
    }
  }

  const emailLinkSubject = `A new activity report is ready for review`

  const emailLinkBody = `Please view your ${
    report?.name
  } by following the link below. The report remains live for 1 week. \r\n \r\n${
    tokenData?.shareLink
  } \r\n \r\nAccess Key: ${
    tokenData?.accessKey
  } \r\n \r\nIf you wish to save the report please print it to a file or hardcopy before ${formatToUSDate(
    tokenData?.expiryDate
  )} \r\n \r\nIf you have any questions about your report, please contact ${
    partnerProfile?.name
  } \r\n`

  const handleHideBrandingChange = (e: ChangeEvent<HTMLInputElement>) => {
    /**
     * onchange event bubbles up to the form onchange on ReportFilterForm
     * we remove filters on the onchange which causes the ShareReport popup to rerender
     * because of the rerender this modal closes, using stopPropogation prevents event bubbling
     */
    e.stopPropagation()
    setHideBranding(e.target.checked)
  }

  return (
    <>
      <Modal open={open} setOpen={setOpen} size="sm">
        <Modal.Header>
          <Modal.TitleBar>
            <Icon icon={faLink} />
            <Heading type="h2">Share Report</Heading>
          </Modal.TitleBar>
        </Modal.Header>
        <Modal.Body>
          <div className="max-w-prose space-y-2">
            {linkGenerated ? (
              <>
                <GeneratedLinkBody tokenData={tokenData} />
                <div className="mt-4 space-y-2">
                  <Heading type="h2">Email Instructions</Heading>
                  <div className="max-w-prose">
                    <ol className="list-none space-y-1">
                      <li className="flex items-start gap-1">
                        <Text>1.</Text>
                        <Text className="m-0 leading-snug text-midnight-800">
                          Clicking the email buttons will open your default
                          email provider. In case it is not configured, you
                          would have to manually copy the link and the access
                          key to send it to your organization
                        </Text>
                      </li>
                      <li className="flex items-start gap-1">
                        <Text>2.</Text>
                        <Text className="m-0 leading-snug text-midnight-800">
                          Enter your organization's email in the to field. You
                          may also add cc and bcc.
                        </Text>
                      </li>
                      <li className="flex items-start gap-1">
                        <Text>3.</Text>
                        <Text className="m-0 leading-snug text-midnight-800">
                          Subject and Body is already prefilled, however you may
                          modify it.
                        </Text>
                      </li>
                    </ol>
                  </div>
                </div>
              </>
            ) : (
              <ShareReportInfo
                daysToExpire={daysToExpire}
                onDaysToExpireChange={(value: number) => setDaysToExpire(value)}
              />
            )}
          </div>
        </Modal.Body>

        {linkGenerated && (
          <Modal.Footer>
            <Link
              href={`${htmlEmailBuilder({
                subject: emailLinkSubject,
                body: emailLinkBody,
              })}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button kind={ButtonType.SOLID_BRAND} Icon={faEnvelope}>
                Email Report Link
              </Button>
            </Link>
          </Modal.Footer>
        )}

        {!linkGenerated && (
          <Modal.Footer>
            <Modal.FooterLeft>
              <div className="flex items-center gap-1.5">
                <CheckboxField
                  checked={hideBranding}
                  id="hideBranding"
                  label="Hide Partner Branding"
                  onChange={handleHideBrandingChange}
                  tooltip={{
                    content:
                      'By selecting "Hide Partner Branding" all logos and custom colors of the partner will be removed from the report.',
                    placement: 'top-center',
                  }}
                />
              </div>
            </Modal.FooterLeft>
            <Modal.FooterRight>
              <Button
                Icon={loading ? undefined : faLink}
                onClick={onGenerateSecureLink}
                disabled={loading}
                loading={loading}
                loadingText="Generating"
              >
                Generate Secure Link
              </Button>
            </Modal.FooterRight>
          </Modal.Footer>
        )}
      </Modal>
    </>
  )
}
